/**
 * Opt-in, in-memory diagnostics for the direct-secret lifecycle.
 *
 * This deliberately stores only a small allow-listed event vocabulary. Do not
 * add room/device identifiers, message content, ciphertext, key material,
 * tokens, signatures, or arbitrary error text to this format.
 */
export const DEFAULT_SECRET_DIAGNOSTICS_LIMIT = 64;
export const MAX_SECRET_DIAGNOSTICS_LIMIT = 128;
function normalizeLimit(value) {
    if (!Number.isFinite(value))
        return DEFAULT_SECRET_DIAGNOSTICS_LIMIT;
    return Math.min(MAX_SECRET_DIAGNOSTICS_LIMIT, Math.max(1, Math.floor(value)));
}
/**
 * Creates an in-memory recorder. `enabled` is intentionally false by default
 * so ordinary production sessions do not retain even the bounded event list.
 */
export function createSecretDiagnosticsRecorder(options = {}) {
    const enabled = options.enabled === true;
    const maxEvents = normalizeLimit(options.maxEvents);
    let events = [];
    let sequence = 0;
    const startedAt = Date.now();
    const sanitize = (input) => {
        if (!input || typeof input !== 'object')
            return null;
        switch (input.kind) {
            case 'socket':
                if (input.event === 'connected' ||
                    input.event === 'disconnected' ||
                    input.event === 'reconnect_scheduled') {
                    return { kind: 'socket', event: input.event };
                }
                return null;
            case 'readiness_invalidated':
                if (input.reason === 'peer_advertisement_identity' ||
                    input.reason === 'peer_advertisement_epoch' ||
                    input.reason === 'peer_advertisement_expiry' ||
                    input.reason === 'local_key_rotation' ||
                    input.reason === 'auth_reset' ||
                    input.reason === 'scope_reset') {
                    return { kind: 'readiness_invalidated', reason: input.reason };
                }
                return null;
            case 'advertisement':
                if (input.event === 'observed' || input.event === 'expired' || input.event === 'rejected') {
                    return { kind: 'advertisement', event: input.event };
                }
                if (input.event === 'identity_changed') {
                    if (input.change !== 'epoch' && input.change !== 'key' && input.change !== 'both') {
                        return null;
                    }
                    return { kind: 'advertisement', event: input.event, change: input.change };
                }
                return null;
            case 'consent':
                if (input.transition === 'request_sent' ||
                    input.transition === 'request_received' ||
                    input.transition === 'approved' ||
                    input.transition === 'ready' ||
                    input.transition === 'reset') {
                    return { kind: 'consent', transition: input.transition };
                }
                return null;
            default:
                return null;
        }
    };
    return {
        record(input) {
            if (!enabled)
                return;
            const safeInput = sanitize(input);
            if (!safeInput)
                return;
            sequence += 1;
            events.push(Object.freeze({
                ...safeInput,
                sequence,
                elapsedMs: Math.max(0, Date.now() - startedAt),
            }));
            if (events.length > maxEvents) {
                events = events.slice(-maxEvents);
            }
        },
        snapshot() {
            return events.map((event) => ({ ...event }));
        },
        clear() {
            events = [];
        },
    };
}
